import type { CreateFirmInput, UpdateFirmInput } from '@financiar/shared';
import { isValidCui } from '@financiar/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Drawer, Form, Space, Tabs, Tooltip } from 'antd';
import { useEffect, useRef } from 'react';
import { getApiError } from '../../api/client';
import { fetchAnafCompany } from '../clienti/api';
import { applyServerErrors, normalizeCui, undefinedIfEmpty } from '../../lib/forms';
import { fetchFirma, createFirma, updateFirma } from './api';
import { FirmaBankAccountsTab } from './FirmaBankAccountsTab';
import { FirmaDateFields } from './FirmaDateFields';
import { FirmaEfacturaTab } from './FirmaEfacturaTab';
import { FirmaLogoTab } from './FirmaLogoTab';
import { FirmaVatPeriodsTab } from './FirmaVatPeriodsTab';

interface FirmFormValues {
  name: string;
  cui: string;
  regCom?: string;
  addressLine: string;
  countyCode: string;
  cityName: string;
  postalCode?: string;
  email?: string;
  phone?: string;
  capitalSocial?: string;
  defaultDueDays: number;
  defaultInvoiceNotes?: string;
  isActive: boolean;
  taxRegime: 'MICRO_1' | 'MICRO_3' | 'PROFIT_16';
}

const CREATE_DEFAULTS: Partial<FirmFormValues> = { defaultDueDays: 30, isActive: true, taxRegime: 'MICRO_1' };

/**
 * `/firme`'s create/edit Drawer. Create mode is just the base "Date firmă"
 * fields; edit mode wraps them in Tabs alongside TVA/Conturi bancare/Logo
 * (those three only make sense once the firm has an id to attach to). The
 * Drawer's own "Salvează" button always submits the base form regardless of
 * which tab is active — AntD's `Tabs` keeps every pane mounted by default,
 * so `form.submit()` works no matter which one the user is looking at.
 */
export function FirmaFormDrawer({
  open,
  editingId,
  onClose,
  initialTab,
}: {
  open: boolean;
  editingId: string | null;
  onClose: () => void;
  /** Which edit-mode tab to show first (e.g. 'efactura' after the ANAF OAuth
   * redirect). Defaults to 'date'. */
  initialTab?: string;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<FirmFormValues>();
  const isEdit = editingId !== null;

  const { data: firma, isLoading: isLoadingFirma } = useQuery({
    queryKey: ['firms', 'detail', editingId],
    queryFn: () => fetchFirma(editingId as string),
    enabled: open && isEdit,
  });

  // Tracks which "session" (the editingId being edited, or the '__create__'
  // sentinel) the form's fields currently hold server-synced values for.
  // TVA/Conturi bancare/Logo mutations invalidate the whole `['firms']`
  // branch by design (so FirmeListPage's table and the header selector
  // refetch too) — that includes THIS drawer's own `['firms','detail',
  // editingId]` query, which re-resolves with a new `firma` object identity
  // on every sibling-tab mutation while the drawer stays open on the "Date
  // firmă" tab. Without this guard, the effect below would fire on every
  // such refetch and silently overwrite whatever the user is mid-typing in
  // "Date firmă" with the (unrelated) fresh server values — see the review
  // finding this fixed. The guard makes the sync fire exactly once per
  // "drawer opened for this firm/create session": `syncedForRef` is reset
  // to `null` the moment the drawer closes, so the *next* open (same firm,
  // a different firm, or create) always resyncs fresh — but a same-session
  // refetch triggered by a sibling tab's mutation does not.
  const syncedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      syncedForRef.current = null;
      return;
    }
    const sessionId = editingId ?? '__create__';
    if (syncedForRef.current === sessionId) return;

    if (isEdit) {
      if (!firma) return; // wait for the detail fetch to resolve before syncing
      form.setFieldsValue({
        name: firma.name,
        cui: firma.cui,
        regCom: firma.regCom ?? undefined,
        addressLine: firma.addressLine,
        countyCode: firma.countyCode,
        cityName: firma.cityName,
        postalCode: firma.postalCode ?? undefined,
        email: firma.email ?? undefined,
        phone: firma.phone ?? undefined,
        capitalSocial: firma.capitalSocial ?? undefined,
        defaultDueDays: firma.defaultDueDays,
        defaultInvoiceNotes: firma.defaultInvoiceNotes ?? undefined,
        isActive: firma.isActive,
        taxRegime: firma.taxRegime,
      });
    } else {
      form.resetFields();
      form.setFieldsValue(CREATE_DEFAULTS);
    }
    syncedForRef.current = sessionId;
  }, [open, isEdit, editingId, firma, form]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['firms'] });

  const createMutation = useMutation({
    mutationFn: (values: CreateFirmInput) => createFirma(values),
    onSuccess: () => {
      message.success('Firma a fost creată.');
      invalidate();
      onClose();
    },
    onError: (error) => {
      const apiError = getApiError(error);
      if (!applyServerErrors(form, apiError)) message.error(apiError?.message ?? 'Firma nu a putut fi creată.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (values: UpdateFirmInput) => updateFirma(editingId as string, values),
    onSuccess: () => {
      message.success('Firma a fost actualizată.');
      invalidate();
    },
    onError: (error) => {
      const apiError = getApiError(error);
      if (!applyServerErrors(form, apiError)) message.error(apiError?.message ?? 'Firma nu a putut fi actualizată.');
    },
  });

  function handleFinish(values: FirmFormValues) {
    const payload = {
      name: values.name.trim(),
      cui: normalizeCui(values.cui),
      regCom: undefinedIfEmpty(values.regCom),
      addressLine: values.addressLine.trim(),
      cityName: values.cityName,
      countyCode: values.countyCode,
      postalCode: undefinedIfEmpty(values.postalCode),
      email: undefinedIfEmpty(values.email),
      phone: undefinedIfEmpty(values.phone),
      capitalSocial: values.capitalSocial || undefined,
      defaultDueDays: values.defaultDueDays,
      defaultInvoiceNotes: undefinedIfEmpty(values.defaultInvoiceNotes),
      isActive: values.isActive,
      taxRegime: values.taxRegime,
    };
    if (isEdit) updateMutation.mutate(payload as UpdateFirmInput);
    else createMutation.mutate(payload as CreateFirmInput);
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // "Preia date ANAF": interoghează registrul public după CUI și precompletează
  // câmpurile firmei. Suprascrie doar câmpurile pe care ANAF le returnează (cele
  // null rămân neatinse). Județul se setează înaintea localității, de care
  // depinde `cityName` (Select de sector la RO-B). Statutul de plătitor de TVA
  // se administrează în fila TVA (perioade), deci aici e doar semnalat.
  const anafMutation = useMutation({
    mutationFn: (cui: string) => fetchAnafCompany(cui),
    onSuccess: (data) => {
      if (data.countyCode) form.setFieldValue('countyCode', data.countyCode);
      const patch: Partial<FirmFormValues> = { name: data.name || form.getFieldValue('name') };
      if (data.regCom) patch.regCom = data.regCom;
      if (data.addressLine) patch.addressLine = data.addressLine;
      if (data.cityName) patch.cityName = data.cityName;
      if (data.postalCode) patch.postalCode = data.postalCode;
      form.setFieldsValue(patch);
      message.success(
        data.isVatPayer
          ? 'Datele au fost preluate din registrul ANAF. Firma este plătitoare de TVA — setează perioada în fila TVA.'
          : 'Datele au fost preluate din registrul ANAF.',
      );
    },
    onError: (error) => {
      const apiError = getApiError(error);
      message.error(apiError?.message ?? 'Datele nu au putut fi preluate de la ANAF.');
    },
  });

  function handleAnafLookup() {
    const cui = normalizeCui(form.getFieldValue('cui') ?? '');
    if (!isValidCui(cui)) {
      message.warning('Introdu un CUI valid înainte de a prelua datele.');
      return;
    }
    anafMutation.mutate(cui);
  }

  const dateForm = (
    <Form<FirmFormValues> form={form} layout="vertical" onFinish={handleFinish} disabled={isEdit && isLoadingFirma}>
      <FirmaDateFields form={form} />
    </Form>
  );

  return (
    <Drawer
      title={isEdit ? 'Editează firma' : 'Adaugă firmă'}
      width="clamp(560px, 40vw, 960px)"
      open={open}
      onClose={onClose}
      destroyOnHidden
      extra={
        <Space>
          <Tooltip title="Completează câmpurile din registrul public ANAF, după CUI">
            <Button loading={anafMutation.isPending} onClick={handleAnafLookup}>
              Preia date ANAF
            </Button>
          </Tooltip>
          <Button onClick={onClose}>Închide</Button>
          <Button type="primary" onClick={() => form.submit()} loading={isSaving}>
            Salvează
          </Button>
        </Space>
      }
    >
      {editingId !== null ? (
        <Tabs
          defaultActiveKey={initialTab ?? 'date'}
          items={[
            { key: 'date', label: 'Date firmă', children: dateForm },
            {
              key: 'tva',
              label: 'TVA',
              children: <FirmaVatPeriodsTab firmaId={editingId} periods={firma?.vatPeriods ?? []} />,
            },
            {
              key: 'conturi',
              label: 'Conturi bancare',
              children: <FirmaBankAccountsTab firmaId={editingId} accounts={firma?.bankAccounts ?? []} />,
            },
            {
              key: 'logo',
              label: 'Logo',
              children: <FirmaLogoTab firmaId={editingId} hasLogo={!!firma?.logoMime} />,
            },
            {
              key: 'efactura',
              label: 'e-Factura',
              children: <FirmaEfacturaTab firmaId={editingId} />,
            },
          ]}
        />
      ) : (
        dateForm
      )}
    </Drawer>
  );
}
